import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogContent, DialogActions, Box, Typography } from '@mui/material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';
import PrintIcon from '@mui/icons-material/Print';
import ReactDOM from 'react-dom/client';

// Component to render a single label
const LabelContent = ({ orderData }) => {
  const receiverData = orderData?.secondaryReceiver;
  const receiverAddressData = receiverData?.personalAddress?.[0] || {};

  const formatAddressLabel = () => {
    const parts = [];
    if (receiverAddressData.address1) parts.push(receiverAddressData.address1);
    if (receiverAddressData.city) parts.push(receiverAddressData.city);
    if (receiverAddressData.state) parts.push(receiverAddressData.state);
    if (receiverAddressData.zipCode) parts.push(receiverAddressData.zipCode);
    return parts.join(', ') || 'Tbilisi';
  };

  const getReceiveDateLabel = () => {
    if (orderData?.orderHistory?.length) {
      const usWarehouseEntry = orderData.orderHistory.find(
        (h) =>
          h.status === 'RECEIVED IN US WAREHOUSE' || h.new_status === 'RECEIVED IN US WAREHOUSE'
      );
      if (usWarehouseEntry) {
        return format(new Date(usWarehouseEntry.createdAt || usWarehouseEntry.date), 'MM/dd/yyyy');
      }
    }
    return orderData?.createdAt ? format(new Date(orderData.createdAt), 'MM/dd/yyyy') : '-';
  };

  const trackingNumberLabel = orderData?.tracking_number || 'N/A';
  const receiverNameLabel = `${receiverData.first_name || '-'} ${
    receiverData.last_name || ''
  }`.trim();
  const receiverPhoneLabel = receiverData.phone || '-';
  const addressLabel = formatAddressLabel();
  const receiveDateLabel = getReceiveDateLabel();
  const weightLabel = orderData?.weight || '-';
  const volumeLabel = orderData?.volume || '-';

  return (
    <div
      style={{
        width: '101.6mm',
        height: '152.4mm',
        padding: '10px',
        backgroundColor: 'white',
        color: 'black',
        fontFamily: 'Arial, sans-serif',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {/* Header with Logo and Barcode */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '8px',
          borderBottom: '2px solid #000',
        }}
      >
        <div
          style={{
            width: '50px',
            height: '50px',
            border: '2px solid #000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            fontWeight: 'bold',
          }}
        >
          R
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Barcode
            value={trackingNumberLabel}
            width={1.5}
            height={40}
            fontSize={12}
            displayValue={false}
          />
          <div style={{ fontSize: '16px', fontWeight: 'bold', letterSpacing: '1px' }}>
            {trackingNumberLabel}
          </div>
        </div>
      </div>

      {/* Company Info */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          fontSize: '11px',
        }}
      >
        <span style={{ fontSize: '20px' }}>🏢</span>
        <div>
          <div style={{ fontWeight: 'bold' }}>41 Tashkent St, Tbilisi</div>
          <div style={{ color: '#666' }}>Tel +995 32 247 0022</div>
        </div>
      </div>

      {/* Receiver Info with QR Code */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '10px 0',
        }}
      >
        <div>
          <h3
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              margin: '0 0 4px 0',
              textTransform: 'lowercase',
            }}
          >
            {receiverNameLabel}
          </h3>
          <p style={{ fontSize: '12px', color: '#444', margin: '2px 0' }}>{addressLabel}</p>
          <p style={{ fontSize: '12px', color: '#444', margin: '2px 0' }}>
            Tel {receiverPhoneLabel}
          </p>
        </div>
        <QRCodeSVG value={trackingNumberLabel} size={80} level="M" />
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '4px 0' }} />

      {/* Dates Section */}
      <div
        style={{
          border: '1px solid #000',
          borderRadius: '4px',
          padding: '8px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>
          RECEIVE DATE
        </div>
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
          {receiveDateLabel}
        </div>
      </div>

      {/* Details Section */}
      <div
        style={{
          display: 'flex',
          border: '1px solid #000',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{ flex: 1, padding: '8px', textAlign: 'center', borderRight: '1px solid #000' }}
        >
          <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>WEIGHT</div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
            {weightLabel}
          </div>
        </div>
        <div style={{ flex: 1, padding: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>VOLUME</div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
            {volumeLabel}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          backgroundColor: '#000',
          color: 'white',
          textAlign: 'center',
          padding: '10px',
          borderRadius: '4px',
          marginTop: 'auto',
        }}
      >
        <div style={{ fontSize: '16px', fontWeight: 'bold', letterSpacing: '1px' }}>
          www.gzavnili.com
        </div>
      </div>
    </div>
  );
};

LabelContent.propTypes = {
  orderData: PropTypes.object.isRequired,
};

function PrintLabel({ open, onClose, order, orders }) {
  const ref = useRef(null);
  const isMultiple = orders && orders.length > 0;

  const receiver = order?.primaryReceiver || order?.secondaryReceiver || {};
  const receiverAddress = receiver?.personalAddress?.[0] || {};

  const formatAddress = () => {
    const parts = [];
    if (receiverAddress.address1) parts.push(receiverAddress.address1);
    if (receiverAddress.city) parts.push(receiverAddress.city);
    if (receiverAddress.state) parts.push(receiverAddress.state);
    if (receiverAddress.zipCode) parts.push(receiverAddress.zipCode);
    return parts.join(', ') || 'Tbilisi';
  };

  const getReceiveDate = () => {
    if (order?.orderHistory?.length) {
      const usWarehouseEntry = order.orderHistory.find(
        (h) =>
          h.status === 'RECEIVED IN US WAREHOUSE' || h.new_status === 'RECEIVED IN US WAREHOUSE'
      );
      if (usWarehouseEntry) {
        return format(new Date(usWarehouseEntry.createdAt || usWarehouseEntry.date), 'MM/dd/yyyy');
      }
    }
    return order?.createdAt ? format(new Date(order.createdAt), 'MM/dd/yyyy') : '-';
  };

  const trackingNumber = order?.tracking_number || 'N/A';
  const receiverName = `${receiver.first_name || '-'} ${receiver.last_name || ''}`.trim();
  const receiverPhone = receiver.phone || '-';
  const address = formatAddress();
  const receiveDate = getReceiveDate();
  const weight = order?.weight || '-';
  const volume = order?.volume || '-';

  const generateAndPrintPDF = async () => {
    if (isMultiple && orders) {
      // Handle multiple orders
      try {
        // eslint-disable-next-line new-cap
        const pdf = new jsPDF({
          orientation: 'p',
          unit: 'mm',
          format: [101.6, 152.4], // 4in x 6in in mm
        });

        // Process orders sequentially
        const processOrder = async (orderData, index) => {
          // Create a temporary container for this order's label
          const tempContainer = document.createElement('div');
          tempContainer.style.position = 'absolute';
          tempContainer.style.left = '-9999px';
          tempContainer.style.top = '0px';
          tempContainer.style.width = '101.6mm';
          tempContainer.style.height = '152.4mm';
          document.body.appendChild(tempContainer);

          // Render React component into the container
          const root = ReactDOM.createRoot(tempContainer);
          root.render(<LabelContent orderData={orderData} />);

          // Wait for React to render and barcode/QR code to be ready
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Capture as canvas
          const canvas = await html2canvas(tempContainer, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
          });
          const imgData = canvas.toDataURL('image/png');

          // Clean up
          root.unmount();
          document.body.removeChild(tempContainer);

          // Add page if not first
          if (index > 0) {
            pdf.addPage();
          }

          const imgProps = pdf.getImageProperties(imgData);
          let imgWidth = imgProps.width;
          let imgHeight = imgProps.height;

          // Scale to fit
          const scaleX = 101.6 / imgWidth;
          const scaleY = 152.4 / imgHeight;
          const scale = Math.min(scaleX, scaleY);
          imgWidth *= scale;
          imgHeight *= scale;

          // Center the image
          const x = (101.6 - imgWidth) / 2;
          const y = (152.4 - imgHeight) / 2;

          pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
        };

        // Process all orders sequentially
        for (let i = 0; i < orders.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await processOrder(orders[i], i);
        }

        const pdfBlob = pdf.output('blob');
        const url = URL.createObjectURL(pdfBlob);
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            try {
              printWindow.print();
            } catch (e) {
              console.error('Error triggering print dialog:', e);
            }
          };
        }
        onClose();
      } catch (error) {
        console.error('Error generating or printing PDF:', error);
      }
    } else if (ref.current) {
      // Handle single order (original logic)
      try {
        const canvas = await html2canvas(ref.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
        });
        const imgData = canvas.toDataURL('image/png');

        // Create PDF with 4x6 inch dimensions (converted to mm: 101.6 x 152.4)
        // eslint-disable-next-line new-cap
        const pdf = new jsPDF({
          orientation: 'p',
          unit: 'mm',
          format: [101.6, 152.4], // 4in x 6in in mm
        });

        const imgProps = pdf.getImageProperties(imgData);
        let imgWidth = imgProps.width;
        let imgHeight = imgProps.height;

        // Scale to fit
        const scaleX = 101.6 / imgWidth;
        const scaleY = 152.4 / imgHeight;
        const scale = Math.min(scaleX, scaleY);
        imgWidth *= scale;
        imgHeight *= scale;

        // Center the image
        const x = (101.6 - imgWidth) / 2;
        const y = (152.4 - imgHeight) / 2;

        pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);

        const pdfBlob = pdf.output('blob');
        const url = URL.createObjectURL(pdfBlob);
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            try {
              printWindow.print();
            } catch (e) {
              console.error('Error triggering print dialog:', e);
            }
          };
        }
        onClose();
      } catch (error) {
        console.error('Error generating or printing PDF:', error);
      }
    }
  };

  if (!order && !isMultiple) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        {isMultiple ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ready to print {orders.length} label{orders.length !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click Print to generate PDF with all selected labels
            </Typography>
          </Box>
        ) : (
          /* Hidden print content */
          <div
            ref={ref}
            style={{
              width: '101.6mm',
              height: '152.4mm',
              padding: '10px',
              backgroundColor: 'white',
              color: 'black',
              fontFamily: 'Arial, sans-serif',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {/* Header with Logo and Barcode */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingBottom: '8px',
                borderBottom: '2px solid #000',
              }}
            >
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  border: '2px solid #000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  fontWeight: 'bold',
                }}
              >
                R
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Barcode
                  value={trackingNumber}
                  width={1.5}
                  height={40}
                  fontSize={12}
                  displayValue={false}
                />
                <div style={{ fontSize: '16px', fontWeight: 'bold', letterSpacing: '1px' }}>
                  {trackingNumber}
                </div>
              </div>
            </div>

            {/* Company Info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                fontSize: '11px',
              }}
            >
              <span style={{ fontSize: '20px' }}>🏢</span>
              <div>
                <div style={{ fontWeight: 'bold' }}>41 Tashkent St, Tbilisi</div>
                <div style={{ color: '#666' }}>Tel +995 32 247 0022</div>
              </div>
            </div>

            {/* Receiver Info with QR Code */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                padding: '10px 0',
              }}
            >
              <div>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    margin: '0 0 4px 0',
                    textTransform: 'lowercase',
                  }}
                >
                  {receiverName}
                </h3>
                <p style={{ fontSize: '12px', color: '#444', margin: '2px 0' }}>{address}</p>
                <p style={{ fontSize: '12px', color: '#444', margin: '2px 0' }}>
                  Tel {receiverPhone}
                </p>
              </div>
              <QRCodeSVG value={trackingNumber} size={80} level="M" />
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '4px 0' }} />

            {/* Dates Section */}
            <div
              style={{
                border: '1px solid #000',
                borderRadius: '4px',
                padding: '8px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>
                RECEIVE DATE
              </div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
                {receiveDate}
              </div>
            </div>

            {/* Details Section */}
            <div
              style={{
                display: 'flex',
                border: '1px solid #000',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  flex: 1,
                  padding: '8px',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}
              >
                <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>
                  WEIGHT
                </div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
                  {weight}
                </div>
              </div>
              <div style={{ flex: 1, padding: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>
                  VOLUME
                </div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
                  {volume}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                backgroundColor: '#000',
                color: 'white',
                textAlign: 'center',
                padding: '10px',
                borderRadius: '4px',
                marginTop: 'auto',
              }}
            >
              <div style={{ fontSize: '16px', fontWeight: 'bold', letterSpacing: '1px' }}>
                www.gzavnili.com
              </div>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={generateAndPrintPDF}>
          {isMultiple ? `Print ${orders.length} Label${orders.length !== 1 ? 's' : ''}` : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PrintLabel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  order: PropTypes.object,
  orders: PropTypes.array,
};

export default PrintLabel;
